// MARK://dhk8/pos/App
// Router for the POS shell. ONE surface, one bundle:
//   /login   → auth (PIN-first + email fallback).
//   /*       → the real POS flow: Floor → Order → Pay/Receipt → Cashcut …
//              Each route is filled in sprint-by-sprint (PORT-FROM-SC-RESTAURANT.md).
//
// There is no separate "demo" route any more — the old `DemoFlow.tsx` surface
// was reabsorbed into the one shell as **training mode** (see
// `lib/training-mode.ts` + `components/TrainingModeBadge.tsx`). Open the app
// with `?mode=training` to land in the same `PosLayout` with a loud banner.
//
// Route access is governed centrally by `lib/role-license-gate.ts`:
//   - kitchen-role users with a KDS license are locked to `/kds`;
//   - `/kds` is forbidden when the tenant has no KDS license;
//   - license-gated routes (`/reports`, `/settings/printing`) redirect when the
//     tenant lacks the feature.
//
// Providers (QueryClient, BrowserRouter) live in main.tsx; training mode is a
// React context provided here so it sits inside the router (it reads ?query).

import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { useAuthStore } from './stores/auth-store.js'
import { getStoredJwt } from './lib/api.js'
import { ErrorBoundary } from './components/ErrorBoundary.js'
import PosLayout from './components/PosLayout.js'
import { TrainingModeProvider } from './lib/training-mode.js'
import { TrainingDataProvider } from './features/training/TrainingDataProvider.js'
import TrainingBanner from './features/training/TrainingBanner.js'
import SandboxBanner from './features/env/SandboxBanner.js'
import GuidedTour, { POS_TOUR_STEPS } from './features/tour/GuidedTour.js'
import RelayJobToaster from './features/RelayJobToaster.js'
import { defaultRouteForUser, resolveRoute } from './lib/role-license-gate.js'
import LoginPage from './pages/LoginPage.js'
import OrderPickupPage from './pages/OrderPickupPage.js'
import BarraDisplayPage from './pages/BarraDisplayPage.js'
import TakeoutOrdersPage from './pages/TakeoutOrdersPage.js'
import PendingOrdersPage from './pages/PendingOrdersPage.js'
import ShiftSelectPage from './pages/ShiftSelectPage.js'
import FloorPage from './pages/FloorPage.js'
import ReservationsPage from './pages/ReservationsPage.js'
import WaitlistPage from './pages/WaitlistPage.js'
import NotificationsPage from './pages/NotificationsPage.js'
import MenuBrowsePage from './pages/MenuBrowsePage.js'
import OrderPage from './pages/OrderPage.js'
import CashoutPage from './pages/CashoutPage.js'
import PettyCashListPage from './pages/PettyCashListPage.js'
import PettyCashDetailPage from './pages/PettyCashDetailPage.js'
import ReportsPage from './pages/ReportsPage.js'
import ReceiptPage from './pages/ReceiptPage.js'
import TicketsPage from './pages/TicketsPage.js'
import ManagerOverridePage from './pages/ManagerOverridePage.js'
import PrintingSettingsPage from './pages/settings/PrintingSettingsPage.js'
import PrintingDiagnosticsPage from './pages/settings/PrintingDiagnosticsPage.js'
import AppearanceSettingsPage from './pages/settings/AppearanceSettingsPage.js'
import OfflineQueuePage from './pages/OfflineQueuePage.js'
import KdsPage from './pages/KdsPage.js'
import FastBarPage from './features/pos/FastBarPage.js'
import BillingPage from './pages/BillingPage.js'
import BillingDetailPage from './pages/BillingDetailPage.js'
import CustomersPage from './pages/CustomersPage.js'
import GlobalDayPage from './pages/GlobalDayPage.js'
import ReportsHubPage from './features/reports/ReportsHubPage.js'
import ReportDetailPage from './pages/ReportDetailPage.js'
import ComponentsGallery from './pages/dev/ComponentsGallery.js'

/** Gate that bounces to /login when there's no authenticated user. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  const location = useLocation()
  // Al recargar la página, hydrate() restaura la sesión desde el JWT en
  // localStorage vía GET /v1/auth/me — pero tarda ~1s. Si redirigimos a /login
  // mientras `user` aún es null, botamos al cajero pese a tener sesión válida.
  // Esperamos mientras hydrate corre (loading) Y hay un JWT persistido.
  if (!user && (loading || getStoredJwt())) {
    return <div className="dhk8-auth-rehydrating" aria-busy="true" />
  }
  if (!user) {
    const returnTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />
  }
  return <>{children}</>
}

/** Gate that bounces to the user's default route when role/license forbids this one. */
function RequireRouteAccess({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()
  const target = resolveRoute(location.pathname, user)
  if (target !== location.pathname) return <Navigate to={target} replace />
  return <>{children}</>
}

/** Wraps a page in the POS shell (header + side nav) behind the auth + access gates.
 *  The page is inside an ErrorBoundary so a render crash keeps the shell usable. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <RequireRouteAccess>
        <PosLayout>
          <ErrorBoundary>
            <SandboxBanner />
            <TrainingBanner />
            {children}
          </ErrorBoundary>
          {/* Onboarding coachmarks — only renders when `?tour=1` (and not yet seen). */}
          <GuidedTour steps={POS_TOUR_STEPS} />
          {/* Seguimiento de impresiones por la nube (Print Relay): muestra
              enviado→imprimiendo→impreso/falló por cada job encolado en mode
              'relay'. Invisible en mode 'host' (loopback no genera jobId). */}
          <RelayJobToaster />
        </PosLayout>
      </RequireRouteAccess>
    </RequireAuth>
  )
}

/** Lands the user on the right home route for their role + license. */
function HomeRedirect() {
  const user = useAuthStore((s) => s.user)
  return <Navigate to={user ? defaultRouteForUser(user) : '/login'} replace />
}

export default function App() {
  return (
    <TrainingModeProvider>
      <TrainingDataProvider>
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />

        {/* Pantalla pública "pedidos listos" — KDS filtrado de cara al cliente.
            FUERA del Shell: sin login. `?tenant=<uuid>` fija el tenant del local. */}
        <Route path="/pedidos-listos" element={<OrderPickupPage />} />

        {/* Pantalla DE CLIENTE para el POS de Barra de 2 pantallas. El operador
            (FastBarPage en /pos/barra) abre esta ruta en una 2ª ventana sobre
            el monitor del cliente; la sincronización va por BroadcastChannel
            (ver hooks/use-cart-broadcast). FUERA del Shell — sin nav, sin login. */}
        <Route path="/barra-display" element={<BarraDisplayPage />} />
        <Route path="/cliente" element={<BarraDisplayPage />} />

        {/* Real POS flow (inside the shell). */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/shift/select" element={<Shell><ShiftSelectPage /></Shell>} />
        <Route path="/floor" element={<Shell><FloorPage /></Shell>} />
        {/* Lane #164 — vistas standalone para reservas y fila (no drawers en /floor). */}
        <Route path="/reservations" element={<Shell><ReservationsPage /></Shell>} />
        <Route path="/waitlist" element={<Shell><WaitlistPage /></Shell>} />
        <Route path="/avisos" element={<Shell><NotificationsPage /></Shell>} />
        {/* OhSir takeout: pedidos para llevar por cobrar — el staff confirma el
            pago y eso libera la orden a cocina (KDS). */}
        <Route path="/para-llevar" element={<Shell><TakeoutOrdersPage /></Shell>} />
        <Route path="/orders/pending" element={<Shell><PendingOrdersPage /></Shell>} />
        <Route path="/menu" element={<Shell><MenuBrowsePage /></Shell>} />
        <Route path="/order" element={<Shell><OrderPage /></Shell>} />
        <Route path="/order/:tableId" element={<Shell><OrderPage /></Shell>} />
        <Route path="/pos/barra" element={<Shell><FastBarPage /></Shell>} />
        <Route path="/cashout" element={<Shell><CashoutPage /></Shell>} />
        <Route path="/cashcut" element={<Shell><CashoutPage /></Shell>} />
        <Route path="/pos/caja" element={<Shell><CashoutPage /></Shell>} />
        {/* Ciclo petty-cash 2-fases: lista de retiros pendientes de conciliar +
            su vista de detalle (conciliar/cancelar). Bajo el mismo prefijo
            /cashout → heredan el gate de módulo "cash" (role-license-gate +
            @dockethub/contracts/shared modules.ts hacen match por prefijo). */}
        <Route path="/cashout/retiros" element={<Shell><PettyCashListPage /></Shell>} />
        <Route path="/cashout/retiros/:id" element={<Shell><PettyCashDetailPage /></Shell>} />
        {/* Operational shift dashboard at /reports; management-reports hub at /reports/hub. */}
        <Route path="/reports" element={<Shell><ReportsPage /></Shell>} />
        <Route path="/reports/hub" element={<Shell><ReportsHubPage /></Shell>} />
        <Route path="/reports/view/:reportId" element={<Shell><ReportDetailPage /></Shell>} />
        {/* Billing (CFDI). Static sub-routes BEFORE /billing/:id so they aren't swallowed. */}
        <Route path="/billing" element={<Shell><BillingPage /></Shell>} />
        <Route path="/billing/customers" element={<Shell><CustomersPage /></Shell>} />
        <Route path="/billing/global-day" element={<Shell><GlobalDayPage /></Shell>} />
        <Route path="/billing/:id" element={<Shell><BillingDetailPage /></Shell>} />
        {/* Tickets: list of every ticket (closed receipts + PRE-CUENTAs) → detail. */}
        <Route path="/tickets" element={<Shell><TicketsPage /></Shell>} />
        <Route path="/tickets/:orderId" element={<Shell><ReceiptPage /></Shell>} />
        {/* Back-compat: the old single-receipt routes still resolve to the detail. */}
        <Route path="/receipt" element={<Shell><ReceiptPage /></Shell>} />
        <Route path="/receipt/:orderId" element={<Shell><ReceiptPage /></Shell>} />
        <Route path="/manager" element={<Shell><ManagerOverridePage /></Shell>} />
        <Route path="/settings/printing" element={<Shell><PrintingSettingsPage /></Shell>} />
        {/* Diagnóstico de impresión — accesible a TODOS los roles (red de seguridad). */}
        <Route path="/diagnostico" element={<Shell><PrintingDiagnosticsPage /></Shell>} />
        <Route path="/settings/appearance" element={<Shell><AppearanceSettingsPage /></Shell>} />
        <Route path="/offline/cola" element={<Shell><OfflineQueuePage /></Shell>} />
        <Route path="/kds" element={<Shell><KdsPage /></Shell>} />

        {/* Galería de componentes (DS-3) — compuerta de revisión, gemelo del catálogo
            de admin-shell. Dev-only: no aparece en el rail de PosLayout (NAV_ITEMS no
            la lista); accesible por URL directa dentro del mismo Shell/gate de rutas. */}
        <Route path="/dev/components" element={<Shell><ComponentsGallery /></Shell>} />

        {/* Unknown route → the user's home (clean redirect, no dead end). */}
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
      </TrainingDataProvider>
    </TrainingModeProvider>
  )
}
